import { AxiosError } from 'axios'
import apiClient from '@app/auth/auth-api-client'
import authLogger from './auth-logger'
import { iframeAuthProxy } from './frame-proxy'

const userBackendApi = apiClient(IDENTITY_SERVICE_URL)
const lenderBackendApi = apiClient(API2_URL)

/**
 * Used when the auth config request fails
 */
export const FALLBACK_AUTH_CONFIG = {
  use_identity_login: true,
  use_frame: false,
}

export const userServiceEndpoints = {
  /**
   * @param {string} username - The username (email) for login
   * @param {string} password - The password for login
   * @param {string} lender_id - The lender account id for login
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN: async (username, password, lender_id, { useProxy = false } = {}) => {
    if (!lender_id) {
      throw new Error('Account selection required')
    }
    if (!username || !password) {
      throw new Error('Username and password are required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login', {
        body: { username, password, lender_id },
      })
      return result.data
    }

    const formData = new FormData()
    formData.append('username', username)
    formData.append('password', password)
    formData.append('lender_id', lender_id)
    const loginResult = await userBackendApi.post('/lender/auth/login', {}, formData, { withCredentials: true })
    return loginResult
  },
  /**
   * @param {string|undefined} loginCode - The login code to establish a session
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} New bearer token with expiration
   */
  LOGIN_CODE: async (loginCode, { useProxy = false } = {}) => {
    if (!loginCode) {
      throw new Error('Login code is required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login-code', {
        body: { code: loginCode },
      })
      return result.data
    }

    const formData = new FormData()
    formData.append('code', loginCode)
    const codeResult = await userBackendApi.post('/lender/auth/login/code', {}, formData, { withCredentials: true })
    return codeResult
  },
  /**
   * @param {string|undefined} loginToken - The bearer token for login
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} The existing bearer token with expiration
   */
  LOGIN_TOKEN: async (loginToken, { useProxy = false } = {}) => {
    if (!loginToken) {
      throw new Error('Login token is required')
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:login-token', {
        body: { token: loginToken },
      })
      return result.data
    }

    const formData = new FormData()
    formData.append('token', loginToken)
    const tokenResult = await userBackendApi.post('/lender/auth/login/token', {}, formData, { withCredentials: true })
    return tokenResult
  },
  /**
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<null>} Returns nothing
   */
  LOGOUT: async (currentBearerToken, { useProxy = false } = {}) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    if (useProxy) {
      await iframeAuthProxy.operation('auth:logout', { headers })
      return
    }

    await userBackendApi.post('/lender/auth/logout', {}, {}, { headers, withCredentials: true })
  },
  /**
   * Get the authentication status and token information
   * @param {string|undefined} currentBearerToken - The current bearer token for authorization
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} A valid bearer token with expiration
   */
  STATUS: async (currentBearerToken, { useProxy = false } = {}) => {
    const headers = {}
    if (currentBearerToken) {
      headers.Authorization = `Bearer ${currentBearerToken}`
    }

    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:status', { headers })
      return result.data
    }

    const tokenResult = await userBackendApi.get('/lender/auth/status', {}, { headers, withCredentials: true })
    return tokenResult
  },
  /**
   * Get new bearer token for session
   * @param {Object} [options] - Optional settings
   * @param {boolean} [options.useProxy=false] - Whether to use the iframe proxy
   * @returns {Promise<{access_token: string, expires: number}>} The new bearer token with expiration
   */
  TOKEN: async ({ useProxy = false } = {}) => {
    if (useProxy) {
      const result = await iframeAuthProxy.operation('auth:token')
      return result.data
    }

    const tokenResult = await userBackendApi.post('/lender/auth/token', {}, {}, { withCredentials: true })
    return tokenResult
  },
  /**
   * Get accounts associated with email
   * @param {string} email - The email to look up accounts for
   * @returns {Promise<{id: string, name: string, logo_url: string}[]>} The account options available for login
   */
  ACCOUNT_OPTIONS: async (email) => {
    const lenderAccounts = await userBackendApi.post('/lender/accounts', {}, { email: email }, { withCredentials: false })
    return lenderAccounts
  },
  /**
   * Get login methods for account
   * @param {string} accountId - The account id to look up login methods for
   * @returns {Promise<{type: string, url?: string}[]>} The login methods available for the account
   */
  ACCOUNT_LOGIN_METHODS: async (accountId) => {
    const lenderLoginMethods = await userBackendApi.post(`/lender/accounts/${accountId}/login_methods`, {}, {}, { withCredentials: false })
    return lenderLoginMethods
  },
}

export const lenderBackendEndpoints = {
  LOGIN: async (username, password, lender_id) => {
    try {
      if (!lender_id) {
        throw new Error('Account selection required')
      }
      if (!username || !password) {
        throw new Error('Username and password are required')
      }

      const queryParams = { lender: lender_id }
      const result = await lenderBackendApi.post('/auth', queryParams, { username, password })
      return result
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
  LOGOUT: async (currentBearerToken) => {
    try {
      const headers = {}
      if (currentBearerToken) {
        headers.Authorization = `Bearer ${currentBearerToken}`
      }
      await lenderBackendApi.post('/auth/logout', {}, {}, { headers })
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
  CURRENT_USER: async (currentBearerToken) => {
    try {
      const headers = {}
      if (currentBearerToken) {
        headers.Authorization = `Bearer ${currentBearerToken}`
      }
      const result = await lenderBackendApi.get('/users/current_user', {}, { headers })
      return result
    } catch (error) {
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.error)
      }
      throw error
    }
  },
}

export const authConfigEndpoints = {
  /**
   * Get the authentication config
   * @returns {Promise<{use_identity_login: boolean, use_frame: boolean}>} The authentication status with access token and expiration
   */
  GET_CONFIG: async () => {
    try {
      const result = await userBackendApi.get('/lender/auth/config', {}, { timeout: 1500 })
      return result
    } catch (error) {
      if (error?.code !== AxiosError.ECONNABORTED) {
        authLogger.error('config request failed:', { code: error?.code }, error)
      }
      return FALLBACK_AUTH_CONFIG
    }
  },
}
